import { Controller } from '@hotwired/stimulus'
import { apiURL, getURLParam } from '../utilities'

export default class extends Controller {
  static targets = ['row', 'notice']

  static values = {
    company: String,
    perCredit: String,
    percentMore: String,
    contactSales: String,
    priceUnavailable: String
  }

  connect() {
    this.#loadPrices()
  }

  retry() {
    this.#loadPrices()
  }

  #loadPrices() {
    this.noticeTarget.hidden = true
    this.rowTargets.forEach(row => this.#loadRow(row))
  }

  async #loadRow(row) {
    this.#reset(row)

    const data = await this.#prices(row)

    if (data) {
      this.#showPrices(row, data)
    } else {
      this.#showFetchError(row)
    }

    row.querySelectorAll('[data-loading]').forEach(skeleton => {
      skeleton.setAttribute('data-loading', false)
    })
  }

  async #prices(row) {
    const priceURL = new URL(
      `${apiURL()}/site/comparisons/${this.companyValue}`
    )

    priceURL.searchParams.append('credits', row.dataset.credits)
    priceURL.searchParams.append('country', getURLParam('country'))

    try {
      const response = await fetch(priceURL)

      return response.ok ? await response.json() : null
    } catch {
      return null
    }
  }

  #showPrices(row, data) {
    const ourPrice = data.our_price
    const theirPrice = data.their_price

    row.querySelector('[data-price="ours"]').textContent = ourPrice.formatted
    this.#showPerCredit(row, 'ours', ourPrice, data.currency)

    if (theirPrice) {
      row.querySelector('[data-price="theirs"]').textContent =
        theirPrice.formatted
      this.#showPerCredit(row, 'theirs', theirPrice, data.currency)
      this.#showPercentDiff(row, ourPrice, theirPrice)
    } else {
      const unavailable = row.querySelector('[data-unavailable]')
      unavailable.textContent = data.contact_sales
        ? this.contactSalesValue
        : this.priceUnavailableValue

      row.querySelector('[data-price="theirs"]').hidden = true
      unavailable.hidden = false
      this.#hidePerCredit(row, 'theirs')
    }
  }

  #showPerCredit(row, column, price, currency) {
    const note = row.querySelector(`[data-note="${column}"]`)
    const perCredit = Number(price.raw) / Number(row.dataset.creditCount)

    if (!currency || !Number.isFinite(perCredit)) {
      this.#hidePerCredit(row, column)

      return
    }

    note.textContent = this.perCreditValue.replace(
      '%{price}',
      this.#format(perCredit, currency)
    )

    note.classList.remove('opacity-0')
  }

  #format(amount, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency.iso_code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount)
  }

  #hidePerCredit(row, column) {
    row.querySelector(`[data-note="${column}"]`).parentNode.hidden = true
  }

  // the badge stays hidden unless the competitor costs more
  #showPercentDiff(row, ourPrice, theirPrice) {
    const percentDiff = Math.round((theirPrice.raw / ourPrice.raw) * 100 - 100)

    if (percentDiff <= 0) return

    row.querySelector('[data-badge-text]').textContent =
      this.percentMoreValue.replace('%{percent}', percentDiff)

    row.querySelector('[data-badge]').hidden = false
  }

  #showFetchError(row) {
    row.querySelectorAll('[data-price]').forEach(price => {
      price.hidden = true
    })

    row.querySelectorAll('[data-error]').forEach(error => {
      error.hidden = false
    })

    this.#hidePerCredit(row, 'ours')
    this.#hidePerCredit(row, 'theirs')
    this.noticeTarget.hidden = false
  }

  // back to the loading state so a retry can render any branch again
  #reset(row) {
    row.querySelector('[data-unavailable]').hidden = true
    row.querySelector('[data-badge]').hidden = true

    row.querySelectorAll('[data-price]').forEach(price => {
      price.hidden = false
    })

    row.querySelectorAll('[data-error]').forEach(error => {
      error.hidden = true
    })

    row.querySelectorAll('[data-note]').forEach(note => {
      note.parentNode.hidden = false
      note.classList.add('opacity-0')
    })

    row.querySelectorAll('[data-loading]').forEach(skeleton => {
      skeleton.setAttribute('data-loading', true)
    })
  }
}
